import { useState, useRef, useEffect } from "react";
import useSEO from '@/hooks/useSEO';
import { Link } from "react-router-dom";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import {
  forexBasics,
  tradingPlatforms,
  brokerConcepts,
  xmModules,
  comparisonPrinciples,
  keyComparisonDimensions,
  analysisConcepts,
  riskConcepts,
  forexTools,
  type AcademyTopic,
  type PlatformData,
  type BrokerConcept,
  type XmModuleItem,
  type RiskConcept,
  type ToolItem,
} from "@/mocks/academyData";

/* ------------------------------------------------------------------ */
/*  Shared Components                                                  */
/* ------------------------------------------------------------------ */

function SectionBadge({ icon, label, color = "#F59E0B" }: { icon: string; label: string; color?: string }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-5"
      style={{ borderColor: `${color}/30`, backgroundColor: `${color}/8` }}
    >
      <i className={`${icon} text-xs`} style={{ color }}></i>
      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

function SectionHeader({ badge, badgeIcon, title, subtitle, badgeColor = "#F59E0B" }: {
  badge: string; badgeIcon: string; title: string; subtitle?: string; badgeColor?: string;
}) {
  return (
    <div className="text-center mb-12 md:mb-16">
      <SectionBadge icon={badgeIcon} label={badge} color={badgeColor} />
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#F8FAFC] mb-5 tracking-tight">{title}</h2>
      {subtitle && (
        <p className="text-base text-[#94A3B8] max-w-3xl mx-auto leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function AnimatedCountUp({ target, isActive, suffix = "" }: { target: number; isActive: boolean; suffix?: string }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!isActive) return;
    let startTime: number | null = null;
    const duration = 1200;
    let raf: number;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setDisplay(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    const t = setTimeout(() => { raf = requestAnimationFrame(animate); }, 300);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [target, isActive]);

  return <span ref={ref}>{display.toLocaleString()}{suffix}</span>;
}

/* ------------------------------------------------------------------ */
/*  Module 1: Forex Basics — Expandable Card Grid                       */
/* ------------------------------------------------------------------ */

function ForexBasicsModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 1" badgeIcon="ri-graduation-cap-line" badgeColor="#10B981"
          title="Forex Basics"
          subtitle="Everything you need to understand the forex market from the ground up — from what a pip is to how market sessions drive liquidity. No prior knowledge required."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {forexBasics.map((topic, idx) => {
            const isExpanded = expandedIndex === idx;
            const isHovered = hoveredIndex === idx;
            return (
              <div
                key={topic.term}
                className={`group relative rounded-2xl border transition-all duration-400 cursor-pointer overflow-hidden flex flex-col ${
                  isExpanded ? "lg:col-span-2" : ""
                }`}
                style={{
                  backgroundColor: isExpanded ? "#111827" : isHovered ? "#151C2C" : "#111827",
                  borderColor: isHovered || isExpanded ? "#10B981/40" : "#1F2937",
                  transform: isHovered && !isExpanded ? "translateY(-3px)" : "translateY(0)",
                  animation: isVisible ? `cardIn 0.5s cubic-bezier(0.16,1,0.3,1) ${idx * 0.06}s both` : "none",
                  opacity: isVisible ? undefined : 0,
                }}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => setExpandedIndex(isExpanded ? null : idx)}
              >
                {/* Number indicator */}
                <div className="absolute top-3 right-4 w-6 h-6 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                  <span className="text-[10px] font-black text-[#10B981]">{(idx + 1).toString().padStart(2, "0")}</span>
                </div>

                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <h3 className="text-sm font-bold text-[#F8FAFC] mb-2 pr-8 leading-snug">{topic.term}</h3>
                  <p className="text-xs text-[#94A3B8] leading-relaxed flex-1">{topic.definition}</p>

                  {/* Expand indicator */}
                  <div className={`flex items-center gap-1.5 mt-4 transition-opacity ${isExpanded ? "opacity-100" : "opacity-60"}`}>
                    <span className="text-[9px] font-semibold text-[#10B981]">
                      {isExpanded ? "Show less" : "See example"}
                    </span>
                    <i className={`ri-arrow-down-s-line text-[#10B981] text-xs transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}></i>
                  </div>
                </div>

                {/* Expanded example */}
                <div
                  className="overflow-hidden transition-all duration-400"
                  style={{
                    maxHeight: isExpanded ? "300px" : "0px",
                    opacity: isExpanded ? 1 : 0,
                  }}
                >
                  {topic.example && (
                    <div className="px-5 md:px-6 pb-5 border-t border-[#1F2937] pt-4 mx-5">
                      <div className="flex items-start gap-2">
                        <i className="ri-lightbulb-flash-line text-[#F59E0B] text-sm shrink-0 mt-0.5"></i>
                        <p className="text-xs text-[#94A3B8] leading-relaxed">{topic.example}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 2: Trading Platform Education — Tab Switcher                */
/* ------------------------------------------------------------------ */

function PlatformEducationModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const [activePlatform, setActivePlatform] = useState(tradingPlatforms[0].id);
  const [transitioning, setTransitioning] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  const platform = tradingPlatforms.find((p) => p.id === activePlatform) ?? tradingPlatforms[0];

  const switchPlatform = (id: string) => {
    if (id === activePlatform) return;
    setTransitioning(true);
    setTimeout(() => {
      setActivePlatform(id);
      setTransitioning(false);
    }, 200);
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      {isVisible && (
        <style>{`
          @keyframes featureIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        `}</style>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 2" badgeIcon="ri-smartphone-line" badgeColor="#3B82F6"
          title="Trading Platform Education"
          subtitle="The platform you trade on is your cockpit. Understand the differences between MT4, MT5, cTrader, and the XM app — and which one fits your trading style."
        />

        {/* Platform Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-1 p-1 bg-[#0B0F19] border border-[#1F2937] rounded-2xl mb-10 mx-auto w-fit">
          {tradingPlatforms.map((p) => (
            <button
              key={p.id}
              onClick={() => switchPlatform(p.id)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{
                backgroundColor: activePlatform === p.id ? `${p.color}15` : "transparent",
                color: activePlatform === p.id ? p.color : "#64748B",
                border: activePlatform === p.id ? `1px solid ${p.color}30` : "1px solid transparent",
              }}
            >
              <span
                className="w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-black"
                style={{ backgroundColor: `${p.color}20`, color: p.color }}
              >
                {p.logo}
              </span>
              {p.name}
            </button>
          ))}
        </div>

        {/* Platform Detail */}
        <div
          className="transition-all duration-200"
          style={{ opacity: transitioning ? 0 : 1, transform: transitioning ? "translateY(8px)" : "translateY(0)" }}
        >
          {/* Platform header */}
          <div
            className="rounded-2xl border p-6 md:p-8 mb-6"
            style={{ backgroundColor: `${platform.color}05`, borderColor: `${platform.color}20` }}
          >
            <div className="flex flex-col md:flex-row md:items-start gap-5">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-xl font-black"
                style={{ backgroundColor: `${platform.color}18`, color: platform.color }}
              >
                {platform.logo}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-black text-[#F8FAFC] mb-2">{platform.name}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed mb-4">{platform.tagline}</p>
                <p className="text-xs text-[#64748B] leading-relaxed">{platform.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {platform.bestFor.map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: `${platform.color}10`, color: platform.color, border: `1px solid ${platform.color}20` }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Feature grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {platform.features.map((feat, idx) => {
              const isHovered = hoveredFeature === idx;
              return (
                <div
                  key={feat.label}
                  className="relative bg-[#0B0F19] border border-[#1F2937] rounded-xl p-4 transition-all duration-300 cursor-default overflow-hidden group"
                  style={{
                    borderColor: isHovered ? `${platform.color}30` : "#1F2937",
                    backgroundColor: isHovered ? `${platform.color}06` : "#0B0F19",
                    animation: isVisible ? `featureIn 0.4s ease ${0.1 + idx * 0.05}s both` : "none",
                  }}
                  onMouseEnter={() => setHoveredFeature(idx)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm"
                      style={{ backgroundColor: `${platform.color}12`, color: platform.color }}
                    >
                      <i className={feat.icon}></i>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-[#F8FAFC] mb-1">{feat.label}</h4>
                      <p className="text-[11px] text-[#94A3B8] leading-relaxed">{feat.description}</p>
                    </div>
                  </div>

                  {/* Hover glow bar */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[2px] transition-all duration-300"
                    style={{
                      backgroundColor: platform.color,
                      transform: isHovered ? "scaleX(1)" : "scaleX(0)",
                      transformOrigin: "left",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 3: Forex Broker Education — Hover-Reveal Cards               */
/* ------------------------------------------------------------------ */

function BrokerEducationModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePositions, setMousePositions] = useState<Record<number, { x: number; y: number }>>({});

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, idx: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePositions((prev) => ({ ...prev, [idx]: { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 } }));
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      {isVisible && (
        <style>{`
          @keyframes brokerCardReveal { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
          @keyframes detailSlideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        `}</style>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 3" badgeIcon="ri-bank-line" badgeColor="#F59E0B"
          title="Forex Broker Education"
          subtitle="A broker is not just where you open an account — it is your gateway to the market. Understanding how brokers work is the first step to choosing one that serves your interests, not theirs."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {brokerConcepts.map((concept, idx) => {
            const isHovered = hoveredIndex === idx;
            const mp = mousePositions[idx];
            return (
              <div
                key={concept.title}
                className="group relative rounded-2xl border overflow-hidden transition-all duration-400 cursor-default flex flex-col"
                style={{
                  backgroundColor: isHovered ? `${concept.color}06` : "#111827",
                  borderColor: isHovered ? `${concept.color}35` : "#1F2937",
                  transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                  animation: isVisible ? `brokerCardReveal 0.5s cubic-bezier(0.16,1,0.3,1) ${idx * 0.08}s both` : "none",
                  opacity: isVisible ? undefined : 0,
                }}
                onMouseMove={(e) => handleMouseMove(e, idx)}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => { setHoveredIndex(null); setMousePositions((p) => { const n = { ...p }; delete n[idx]; return n; }); }}
              >
                {/* Mouse-tracking radial glow */}
                {mp && (
                  <div
                    className="absolute inset-0 pointer-events-none transition-opacity duration-200"
                    style={{
                      opacity: isHovered ? 0.16 : 0,
                      background: `radial-gradient(circle 180px at ${mp.x}% ${mp.y}%, ${concept.color}28, transparent 70%)`,
                    }}
                  />
                )}

                {/* Top accent strip */}
                <div className="h-[3px] w-full shrink-0" style={{ backgroundColor: concept.color }} />

                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-base"
                      style={{ backgroundColor: `${concept.color}12`, color: concept.color }}
                    >
                      <i className={concept.icon}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-[#F8FAFC] leading-snug">{concept.title}</h3>
                    </div>
                  </div>

                  <p className="text-xs text-[#94A3B8] leading-relaxed flex-1">{concept.description}</p>

                  {/* Hover reveal detail */}
                  <div
                    className="overflow-hidden transition-all duration-400"
                    style={{ maxHeight: isHovered ? "300px" : "0px", opacity: isHovered ? 1 : 0 }}
                  >
                    <div className="pt-4 mt-3 border-t border-[#1F2937]">
                      <p className="text-[11px] text-[#64748B] leading-relaxed" style={{ animation: isHovered ? "detailSlideUp 0.3s ease both" : "none" }}>
                        {concept.detail}
                      </p>
                    </div>
                  </div>

                  {/* Hover prompt */}
                  <div className="flex items-center gap-1.5 mt-4 text-[9px] text-[#475569]">
                    <i className="ri-mouse-line text-[10px]"></i>
                    {isHovered ? "Keep reading..." : "Hover to learn more"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 4: XM Broker Education — Accordion Panels                    */
/* ------------------------------------------------------------------ */

function XmEducationModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      {isVisible && (
        <style>{`
          @keyframes bulletIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
        `}</style>
      )}

      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 4" badgeIcon="ri-star-line" badgeColor="#EC4899"
          title="XM Broker Education"
          subtitle="XM is the benchmark we compare every other broker against. Understand what XM offers — from regulation and safety to account types, platforms, bonuses, and education — before you compare alternatives."
        />

        <div className="space-y-3">
          {xmModules.map((mod, idx) => {
            const isExpanded = expandedIndex === idx;
            return (
              <div
                key={mod.title}
                className="bg-[#0B0F19] border border-[#1F2937] rounded-2xl overflow-hidden transition-all duration-300"
                style={{ borderColor: isExpanded ? `${mod.color}25` : "#1F2937" }}
              >
                <button
                  onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                  className="w-full flex items-center gap-4 p-5 md:p-6 text-left cursor-pointer"
                >
                  {/* Icon */}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-base transition-colors duration-300"
                    style={{
                      backgroundColor: isExpanded ? `${mod.color}18` : `${mod.color}10`,
                      color: mod.color,
                    }}
                  >
                    <i className={mod.icon}></i>
                  </div>

                  {/* Title + description */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-[#F8FAFC] mb-1">{mod.title}</h3>
                    <p className="text-xs text-[#64748B] line-clamp-1">{mod.description}</p>
                  </div>

                  {/* Expand toggle */}
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300"
                    style={{
                      backgroundColor: isExpanded ? `${mod.color}20` : "#1F2937",
                    }}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                      style={{ color: isExpanded ? mod.color : "#475569" }}
                    ></i>
                  </div>
                </button>

                {/* Expanded content */}
                <div
                  className="overflow-hidden transition-all duration-400"
                  style={{
                    maxHeight: isExpanded ? "600px" : "0px",
                    opacity: isExpanded ? 1 : 0,
                  }}
                >
                  <div className="px-5 md:px-6 pb-5 md:pb-6 pt-2">
                    <div
                      className="rounded-xl p-4 md:p-5"
                      style={{ backgroundColor: `${mod.color}06`, border: `1px solid ${mod.color}12` }}
                    >
                      <ul className="space-y-2.5">
                        {mod.bullets.map((bullet, bi) => (
                          <li
                            key={bi}
                            className="flex items-start gap-2.5 text-xs text-[#94A3B8] leading-relaxed"
                            style={{ animation: isExpanded ? `bulletIn 0.3s ease ${0.05 + bi * 0.04}s both` : "none" }}
                          >
                            <div
                              className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5"
                              style={{ backgroundColor: mod.color }}
                            />
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 5: Broker Comparison Education — Interactive Scorecard      */
/* ------------------------------------------------------------------ */

const scorecardBrokers: {
  name: string; logo: string; color: string; slug: string;
  scores: Record<string, number>; badge: string; verdict: string;
}[] = [
  {
    name: "XM", logo: "/images/xm.jpg", color: "#8B5CF6", slug: "/compare",
    scores: { "Trading Cost": 78, "Regulation": 92, "Execution": 85, "Platforms": 88, "Funding": 94, "Education": 96 },
    badge: "Editor's Choice", verdict: "Exceptional education & bonus ecosystem with strong multi-jurisdiction regulation.",
  },
  {
    name: "IC Markets", logo: "/images/ic.jpg", color: "#3B82F6", slug: "/compare/xm-vs-icmarkets",
    scores: { "Trading Cost": 97, "Regulation": 90, "Execution": 96, "Platforms": 84, "Funding": 78, "Education": 62 },
    badge: "Cost Champion", verdict: "Tightest spreads and fastest ECN execution — the go-to for scalpers and algo traders.",
  },
  {
    name: "Tickmill", logo: "/images/TM.jpg", color: "#10B981", slug: "/compare/xm-vs-tickmill",
    scores: { "Trading Cost": 95, "Regulation": 88, "Execution": 90, "Platforms": 76, "Funding": 72, "Education": 58 },
    badge: "Low Cost ECN", verdict: "Ultra-low all-in costs on Pro account. Best for high-volume cost-sensitive traders.",
  },
  {
    name: "FxPro", logo: "/images/fxpro.jpg", color: "#F59E0B", slug: "/compare/xm-vs-fxpro",
    scores: { "Trading Cost": 74, "Regulation": 94, "Execution": 87, "Platforms": 92, "Funding": 80, "Education": 70 },
    badge: "Platform Variety", verdict: "FCA-regulated with MT4 + MT5 + cTrader. Best for UK/EU traders who want platform choice.",
  },
  {
    name: "FXTM", logo: "/images/fx.jpg", color: "#EC4899", slug: "/compare/xm-vs-fxtm",
    scores: { "Trading Cost": 72, "Regulation": 85, "Execution": 80, "Platforms": 78, "Funding": 82, "Education": 88 },
    badge: "Education Leader", verdict: "200+ free webinars per year and strong emerging-market presence. Great for learners.",
  },
];

const dimLabels = ["Trading Cost", "Regulation", "Execution", "Platforms", "Funding", "Education"];
const dimIcons: Record<string, string> = {
  "Trading Cost": "ri-money-dollar-circle-line",
  "Regulation": "ri-shield-check-line",
  "Execution": "ri-flashlight-line",
  "Platforms": "ri-smartphone-line",
  "Funding": "ri-exchange-funds-line",
  "Education": "ri-book-open-line",
};
const dimColors: Record<string, string> = {
  "Trading Cost": "#10B981",
  "Regulation": "#3B82F6",
  "Execution": "#F59E0B",
  "Platforms": "#EC4899",
  "Funding": "#14B8A6",
  "Education": "#8B5CF6",
};

function ComparisonEducationModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const accent = "#8B5CF6";

  const [activeBroker, setActiveBroker] = useState(0);
  const [activeDim, setActiveDim] = useState<string | null>(null);
  const [animKey, setAnimKey] = useState(0);

  const broker = scorecardBrokers[activeBroker];
  const overallScore = Math.round(
    Object.values(broker.scores).reduce((a, b) => a + b, 0) / Object.values(broker.scores).length
  );

  const handleBrokerSwitch = (idx: number) => {
    if (idx === activeBroker) return;
    setActiveBroker(idx);
    setAnimKey((k) => k + 1);
    setActiveDim(null);
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      {isVisible && (
        <style>{`
          @keyframes scoreBarGrow { from { width:0% } to { width:var(--score-w) } }
          @keyframes scoreFadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
          @keyframes brokerPulse { 0%,100%{opacity:1} 50%{opacity:0.7} }
        `}</style>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 5" badgeIcon="ri-arrow-left-right-line" badgeColor={accent}
          title="Broker Comparison Education"
          subtitle="Good comparison goes beyond a side-by-side table. Learn why comparison matters, what to compare, and how to use our data to find your ideal broker — not the one with the best marketing."
        />

        {/* ── Section A: Interactive Scorecard Studio ── */}
        <div className="mb-20 md:mb-24">
          <div className="text-center mb-8">
            <h3 className="text-lg md:text-xl font-black text-[#F8FAFC] mb-2">Live Broker Scorecard</h3>
            <p className="text-xs text-[#64748B] max-w-lg mx-auto">
              Click any broker below to load their scorecard. Hover a dimension bar to see what that metric actually measures.
            </p>
          </div>

          {/* Broker selector tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {scorecardBrokers.map((b, idx) => (
              <button
                key={b.name}
                onClick={() => handleBrokerSwitch(idx)}
                className="relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{
                  backgroundColor: activeBroker === idx ? `${b.color}14` : "#111827",
                  borderColor: activeBroker === idx ? `${b.color}50` : "#1F2937",
                  transform: activeBroker === idx ? "translateY(-2px)" : "translateY(0)",
                }}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black shrink-0"
                  style={{ backgroundColor: `${b.color}20`, color: b.color }}
                >
                  <img src={b.logo} alt={b.name} className="w-4 h-4 rounded object-contain" />
                </div>
                <span
                  className="text-xs font-bold"
                  style={{ color: activeBroker === idx ? "#F8FAFC" : "#64748B" }}
                >
                  {b.name}
                </span>
                {activeBroker === idx && (
                  <div
                    className="absolute -bottom-[1px] left-4 right-4 h-[2px] rounded-full"
                    style={{ backgroundColor: b.color }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Main scorecard */}
          <div
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: `${broker.color}30`, backgroundColor: "#111827" }}
          >
            {/* Header band */}
            <div
              className="relative px-6 md:px-8 py-5 border-b flex flex-col md:flex-row md:items-center gap-4 overflow-hidden"
              style={{ borderColor: `${broker.color}20`, backgroundColor: `${broker.color}07` }}
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: `radial-gradient(ellipse at 80% 50%, ${broker.color}18, transparent 60%)` }}
              />
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black shrink-0 relative"
                style={{ backgroundColor: `${broker.color}20`, color: broker.color, border: `1px solid ${broker.color}40` }}
              >
                <img src={broker.logo} alt={broker.name} className="w-8 h-8 rounded-lg object-contain" />
              </div>
              <div className="flex-1 relative">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-lg font-black text-[#F8FAFC]">{broker.name}</h4>
                  <span
                    className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${broker.color}18`, color: broker.color }}
                  >
                    {broker.badge}
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8] leading-relaxed max-w-xl">{broker.verdict}</p>
                <a
                  href="#"
                  className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-lg text-xs font-bold transition-all hover:brightness-110 whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: broker.color, color: "#0B0F19" }}
                >
                  Open Account
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
              </div>
              {/* Overall score ring */}
              <div className="relative shrink-0 flex flex-col items-center">
                <div
                  className="text-4xl font-black tabular-nums"
                  key={animKey}
                  style={{ color: broker.color, animation: isVisible ? "scoreFadeUp 0.5s ease both" : "none" }}
                >
                  {overallScore}
                </div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-[#475569] mt-0.5">Overall</div>
                <div className="text-[9px] text-[#374151]">/100</div>
              </div>
            </div>

            {/* Six dimension bars */}
            <div className="p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1">
                {dimLabels.map((dim, idx) => {
                  const score = broker.scores[dim];
                  const c = dimColors[dim];
                  const isHov = activeDim === dim;
                  const principleForDim = comparisonPrinciples.find(
                    (p) => p.title.toLowerCase().includes(dim.split(" ")[0].toLowerCase())
                  ) ?? comparisonPrinciples[idx % comparisonPrinciples.length];
                  return (
                    <div
                      key={dim}
                      className="relative py-3.5 px-3 rounded-xl transition-all duration-200 cursor-pointer"
                      style={{
                        backgroundColor: isHov ? `${c}0D` : "transparent",
                        border: `1px solid ${isHov ? c + "30" : "transparent"}`,
                        animation: isVisible ? `scoreFadeUp 0.45s ease ${0.1 + idx * 0.07}s both` : "none",
                      }}
                      onMouseEnter={() => setActiveDim(dim)}
                      onMouseLeave={() => setActiveDim(null)}
                    >
                      <div className="flex items-center gap-3 mb-2.5">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-sm shrink-0"
                          style={{ backgroundColor: `${c}15`, color: c }}
                        >
                          <i className={dimIcons[dim]}></i>
                        </div>
                        <span className="text-xs font-bold text-[#F8FAFC] flex-1">{dim}</span>
                        <span
                          className="text-sm font-black tabular-nums shrink-0"
                          key={`${animKey}-${dim}`}
                          style={{
                            color: score >= 90 ? "#10B981" : score >= 75 ? "#F59E0B" : "#EF4444",
                            animation: isVisible ? "scoreFadeUp 0.4s ease both" : "none",
                          }}
                        >
                          {score}
                        </span>
                      </div>

                      {/* Score bar */}
                      <div className="h-2 rounded-full bg-[#1F2937] overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            backgroundColor: c,
                            width: isVisible ? `${score}%` : "0%",
                            transition: `width 0.8s cubic-bezier(0.16,1,0.3,1) ${0.2 + idx * 0.07}s`,
                          }}
                          key={`${animKey}-bar-${dim}`}
                        />
                      </div>

                      {/* Always-visible description */}
                      <div
                        className="mt-2.5 rounded-lg p-2.5 border"
                        style={{ backgroundColor: `${c}06`, borderColor: `${c}18` }}
                      >
                        <div className="flex items-start gap-2">
                          <i className="ri-information-line text-xs shrink-0 mt-0.5" style={{ color: c }}></i>
                          <p className="text-[10px] text-[#64748B] leading-relaxed">
                            {principleForDim.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Compare CTA */}
              {broker.name !== "XM" && (
                <div className="mt-6 pt-5 border-t border-[#1F2937] flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black"
                      style={{ backgroundColor: "#8B5CF620", color: "#8B5CF6" }}
                    >
                      XM
                    </div>
                    <i className="ri-arrow-left-right-line text-[#475569] text-sm"></i>
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black"
                      style={{ backgroundColor: `${broker.color}20`, color: broker.color }}
                    >
                      <img src={broker.logo} alt={broker.name} className="w-5 h-5 rounded object-contain" />
                    </div>
                    <span className="text-xs text-[#64748B]">See the full side-by-side breakdown</span>
                  </div>
                  <Link
                    to={broker.slug}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all hover:opacity-90 cursor-pointer"
                    style={{ backgroundColor: `${broker.color}18`, color: broker.color, border: `1px solid ${broker.color}30` }}
                  >
                    Full Comparison <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Dimension legend */}
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            {dimLabels.map((dim) => (
              <div
                key={dim}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full"
                style={{ backgroundColor: `${dimColors[dim]}10`, border: `1px solid ${dimColors[dim]}20` }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dimColors[dim] }} />
                <span className="text-[10px] font-semibold" style={{ color: dimColors[dim] }}>{dim}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section B: 6 Principles — Flip Cards Grid ── */}
        <div>
          <div className="text-center mb-8">
            <h3 className="text-lg md:text-xl font-black text-[#F8FAFC] mb-2">6 Principles of Smart Broker Comparison</h3>
            <p className="text-xs text-[#64748B] max-w-lg mx-auto">
              Hover any card to reveal the insight behind each principle.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {comparisonPrinciples.map((p, idx) => {
              return (
                <FlipCard key={p.step} principle={p} idx={idx} isVisible={isVisible} />
              );
            })}
          </div>
        </div>


      </div>
    </section>
  );
}

function FlipCard({ principle, idx, isVisible }: {
  principle: { step: string; title: string; description: string; icon: string; color: string };
  idx: number;
  isVisible: boolean;
}) {
  const [flipped, setFlipped] = useState(false);
  const parts = principle.title.split(" — ");
  const main = parts[0];
  const sub = parts[1];

  return (
    <div
      className="relative h-52 cursor-pointer"
      style={{
        perspective: "1000px",
        animation: isVisible ? `scoreFadeUp 0.5s cubic-bezier(0.16,1,0.3,1) ${idx * 0.08}s both` : "none",
        opacity: isVisible ? undefined : 0,
      }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <div
        className="relative w-full h-full transition-transform duration-500"
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front face */}
        <div
          className="absolute inset-0 rounded-2xl border flex flex-col p-5 overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            backgroundColor: "#111827",
            borderColor: `${principle.color}25`,
          }}
        >
          <div
            className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl"
            style={{ backgroundColor: principle.color }}
          />
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-base mb-4"
            style={{ backgroundColor: `${principle.color}18`, color: principle.color }}
          >
            <i className={principle.icon}></i>
          </div>
          <div
            className="text-[10px] font-black uppercase tracking-[0.18em] mb-2"
            style={{ color: principle.color }}
          >
            Principle {principle.step}
          </div>
          <h4 className="text-sm font-black text-[#F8FAFC] leading-snug mb-1">{main}</h4>
          {sub && <div className="text-xs font-semibold" style={{ color: principle.color }}>{sub}</div>}
          <div className="mt-auto flex items-center gap-1.5 text-[9px] text-[#475569]">
            <i className="ri-refresh-line text-[10px]"></i>
            Hover to reveal
          </div>
        </div>

        {/* Back face */}
        <div
          className="absolute inset-0 rounded-2xl border flex flex-col p-5 overflow-hidden"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            backgroundColor: `${principle.color}0D`,
            borderColor: `${principle.color}40`,
          }}
        >
          <div
            className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl"
            style={{ backgroundColor: principle.color }}
          />
          <div
            className="text-[10px] font-black uppercase tracking-[0.18em] mb-3"
            style={{ color: principle.color }}
          >
            The Insight
          </div>
          <p className="text-xs text-[#CBD5E1] leading-relaxed flex-1">{principle.description}</p>
          <div
            className="mt-3 inline-flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full self-start"
            style={{ backgroundColor: `${principle.color}18`, color: principle.color }}
          >
            <i className="ri-lightbulb-flash-line text-[10px]"></i>
            Key Takeaway
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 6: Forex Analysis Education — Three-Tab Toggle               */
/* ------------------------------------------------------------------ */

function AnalysisEducationModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const [activeTab, setActiveTab] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [hoveredConcept, setHoveredConcept] = useState<number | null>(null);

  const active = analysisConcepts[activeTab];

  const switchTab = (idx: number) => {
    if (idx === activeTab) return;
    setTransitioning(true);
    setTimeout(() => {
      setActiveTab(idx);
      setTransitioning(false);
    }, 150);
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 6" badgeIcon="ri-line-chart-line" badgeColor="#F97316"
          title="Forex Analysis Education"
          subtitle="Understanding what moves currency prices is the difference between gambling and trading. Explore the three pillars of forex analysis and how professional traders combine them."
        />

        {/* Three-tab toggle */}
        <div className="flex flex-wrap items-center justify-center gap-1 p-1 bg-[#0B0F19] border border-[#1F2937] rounded-2xl mb-10 mx-auto w-fit">
          {analysisConcepts.map((concept, idx) => (
            <button
              key={concept.title}
              onClick={() => switchTab(idx)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{
                backgroundColor: activeTab === idx ? `${concept.color}15` : "transparent",
                color: activeTab === idx ? concept.color : "#64748B",
                border: activeTab === idx ? `1px solid ${concept.color}30` : "1px solid transparent",
              }}
            >
              <i className={`${concept.icon} text-[11px]`}></i>
              {concept.title}
            </button>
          ))}
        </div>

        {/* Content area with transition */}
        <div
          className="transition-all duration-150"
          style={{ opacity: transitioning ? 0 : 1, transform: transitioning ? "translateY(8px)" : "translateY(0)" }}
        >
          {/* Intro card */}
          <div
            className="rounded-2xl border p-6 md:p-8 mb-8 text-center"
            style={{ backgroundColor: `${active.color}05`, borderColor: `${active.color}20` }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl"
              style={{ backgroundColor: `${active.color}15`, color: active.color }}
            >
              <i className={active.icon}></i>
            </div>
            <h3 className="text-xl font-black text-[#F8FAFC] mb-3">{active.title}</h3>
            <p className="text-sm text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">{active.description}</p>
          </div>

          {/* Concepts grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {active.concepts.map((concept, idx) => {
              const isHov = hoveredConcept === idx;
              return (
                <div
                  key={concept.label}
                  className="relative bg-[#0B0F19] border border-[#1F2937] rounded-xl p-5 transition-all duration-300 cursor-default overflow-hidden"
                  style={{ borderColor: isHov ? `${active.color}30` : "#1F2937" }}
                  onMouseEnter={() => setHoveredConcept(idx)}
                  onMouseLeave={() => setHoveredConcept(null)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: active.color }}
                    />
                    <h4 className="text-sm font-bold text-[#F8FAFC]">{concept.label}</h4>
                  </div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{concept.detail}</p>

                  {/* Hover bottom bar */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[2px] transition-all duration-300"
                    style={{
                      backgroundColor: active.color,
                      transform: isHov ? "scaleX(1)" : "scaleX(0)",
                      transformOrigin: "left",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 7: Risk Management — Interactive Dashboard Layout            */
/* ------------------------------------------------------------------ */

function RiskManagementModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const [activeRule, setActiveRule] = useState(0);
  const [panelTransitioning, setPanelTransitioning] = useState(false);
  // Interactive risk calculator state
  const [accountBalance, setAccountBalance] = useState(10000);
  const [riskPercent, setRiskPercent] = useState(1);
  const [stopLossPips, setStopLossPips] = useState(20);
  const [pipValue, setPipValue] = useState(10);

  const maxRisk = accountBalance * (riskPercent / 100);
  const positionSize = stopLossPips > 0 && pipValue > 0 ? maxRisk / (stopLossPips * pipValue) : 0;
  const rewardTarget = maxRisk * 2;

  const active = riskConcepts[activeRule];

  const handleRuleSwitch = (idx: number) => {
    if (idx === activeRule) return;
    setPanelTransitioning(true);
    setTimeout(() => {
      setActiveRule(idx);
      setPanelTransitioning(false);
    }, 160);
  };

  const riskColors = ["#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#EC4899", "#F97316"];

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      {isVisible && (
        <style>{`
          @keyframes ruleNavIn { from { opacity:0; transform:translateX(-16px); } to { opacity:1; transform:translateX(0); } }
          @keyframes statNumIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
          @keyframes calcPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); } 50% { box-shadow: 0 0 0 6px rgba(239,68,68,0.08); } }
        `}</style>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 7" badgeIcon="ri-shield-flash-line" badgeColor="#EF4444"
          title="Risk Management Education"
          subtitle="You can have the best strategy in the world — without risk management, you will lose everything. These principles separate traders who survive from the 70%+ who blow their accounts within 90 days."
        />

        {/* ── Main dashboard: rule navigator + detail panel ── */}
        <div className="flex flex-col lg:flex-row gap-5 mb-8">

          {/* Left: Vertical rule list */}
          <div className="w-full lg:w-72 shrink-0 flex flex-col gap-1.5">
            {riskConcepts.map((concept, idx) => {
              const isActive = activeRule === idx;
              const color = riskColors[idx];
              return (
                <button
                  key={concept.title}
                  onClick={() => handleRuleSwitch(idx)}
                  className="relative flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-left transition-all duration-200 cursor-pointer w-full group"
                  style={{
                    backgroundColor: isActive ? `${color}0F` : "#111827",
                    border: `1px solid ${isActive ? color + "30" : "#1F2937"}`,
                    animation: isVisible ? `ruleNavIn 0.4s ease ${idx * 0.06}s both` : "none",
                    opacity: isVisible ? undefined : 0,
                  }}
                >
                  {/* Active left bar */}
                  <div
                    className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full transition-all duration-200"
                    style={{ backgroundColor: isActive ? color : "transparent" }}
                  />

                  {/* Step number */}
                  <span
                    className="text-[10px] font-black w-5 shrink-0 tabular-nums"
                    style={{ color: isActive ? color : "#475569" }}
                  >
                    {(idx + 1).toString().padStart(2, "0")}
                  </span>

                  {/* Icon bubble */}
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm transition-all duration-200"
                    style={{
                      backgroundColor: isActive ? `${color}18` : `${color}08`,
                      color: isActive ? color : "#475569",
                    }}
                  >
                    <i className={concept.icon}></i>
                  </div>

                  {/* Title */}
                  <span
                    className="text-xs font-semibold leading-snug transition-colors duration-200 flex-1"
                    style={{ color: isActive ? "#F8FAFC" : "#64748B" }}
                  >
                    {concept.title}
                  </span>

                  {/* Chevron */}
                  <i
                    className="ri-arrow-right-s-line text-xs shrink-0 transition-all duration-200"
                    style={{ color: isActive ? color : "transparent" }}
                  ></i>
                </button>
              );
            })}
          </div>

          {/* Right: Detail panel */}
          <div className="flex-1 min-w-0">
            <div
              className="h-full rounded-2xl border overflow-hidden"
              style={{
                borderColor: `${active.color}25`,
                backgroundColor: "#111827",
                opacity: panelTransitioning ? 0 : 1,
                transform: panelTransitioning ? "translateY(8px)" : "translateY(0)",
                transition: "opacity 0.16s ease, transform 0.16s ease",
              }}
            >
              {/* Panel header strip */}
              <div
                className="px-6 md:px-8 py-5 border-b"
                style={{ borderColor: `${active.color}18`, backgroundColor: `${active.color}06` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-xl"
                    style={{ backgroundColor: `${active.color}18`, color: active.color }}
                  >
                    <i className={active.icon}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${active.color}14`, color: active.color }}
                      >
                        Rule {(activeRule + 1).toString().padStart(2, "0")} of {riskConcepts.length}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-black text-[#F8FAFC] mb-2">{active.title}</h3>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{active.description}</p>
                  </div>
                </div>
              </div>

              {/* Panel body: two columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#1F2937]">
                {/* Left col: formula + detail */}
                <div className="p-6 md:p-7">
                  {/* Formula block */}
                  <div
                    className="rounded-xl p-4 mb-5"
                    style={{ backgroundColor: `${active.color}08`, border: `1px solid ${active.color}18` }}
                  >
                    <div className="text-[9px] uppercase tracking-widest font-black mb-2" style={{ color: active.color }}>
                      The Rule
                    </div>
                    <div className="text-sm font-black text-[#F8FAFC] leading-snug font-mono">{active.rule}</div>
                  </div>

                  {/* Detail text */}
                  <div className="flex items-start gap-2 mb-5">
                    <i className="ri-information-line text-sm shrink-0 mt-0.5" style={{ color: active.color }}></i>
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: active.color }}>Why This Matters</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{active.detail}</p>

                  {/* Navigation prev/next */}
                  <div className="flex items-center gap-2 mt-6">
                    <button
                      onClick={() => handleRuleSwitch(Math.max(0, activeRule - 1))}
                      disabled={activeRule === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{ backgroundColor: `${active.color}10`, color: active.color, border: `1px solid ${active.color}20` }}
                    >
                      <i className="ri-arrow-left-s-line text-xs"></i>
                      Prev
                    </button>
                    <button
                      onClick={() => handleRuleSwitch(Math.min(riskConcepts.length - 1, activeRule + 1))}
                      disabled={activeRule === riskConcepts.length - 1}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{ backgroundColor: `${active.color}10`, color: active.color, border: `1px solid ${active.color}20` }}
                    >
                      Next
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </button>
                    <span className="ml-auto text-[10px] text-[#475569]">
                      {activeRule + 1} / {riskConcepts.length}
                    </span>
                  </div>
                </div>

                {/* Right col: all-rules summary dots + progress */}
                <div className="p-6 md:p-7">
                  <div className="text-[9px] font-black uppercase tracking-widest text-[#475569] mb-4">All 6 Principles</div>
                  <div className="space-y-2.5">
                    {riskConcepts.map((concept, idx) => {
                      const isActive = activeRule === idx;
                      const color = riskColors[idx];
                      return (
                        <button
                          key={concept.title}
                          onClick={() => handleRuleSwitch(idx)}
                          className="w-full flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all duration-200 cursor-pointer group text-left"
                          style={{
                            backgroundColor: isActive ? `${color}10` : "transparent",
                            border: `1px solid ${isActive ? color + "25" : "transparent"}`,
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full shrink-0 transition-all duration-200"
                            style={{ backgroundColor: isActive ? color : "#374151", transform: isActive ? "scale(1.4)" : "scale(1)" }}
                          />
                          <span
                            className="text-[11px] font-semibold flex-1 truncate transition-colors duration-200"
                            style={{ color: isActive ? "#F8FAFC" : "#4B5563" }}
                          >
                            {concept.title}
                          </span>
                          <span
                            className="text-[9px] font-mono shrink-0"
                            style={{ color: isActive ? color : "#374151" }}
                          >
                            {(idx + 1).toString().padStart(2, "0")}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Progress bar */}
                  <div className="mt-6">
                    <div className="flex justify-between mb-2">
                      <span className="text-[9px] font-bold text-[#475569] uppercase tracking-wider">Progress</span>
                      <span className="text-[9px] font-bold" style={{ color: active.color }}>
                        {Math.round(((activeRule + 1) / riskConcepts.length) * 100)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#1F2937] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${((activeRule + 1) / riskConcepts.length) * 100}%`,
                          backgroundColor: active.color,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Interactive Risk Calculator ── */}
        <div
          className="rounded-2xl border overflow-hidden"
          style={{ borderColor: "#1F2937", backgroundColor: "#111827", animation: isVisible ? "calcPulse 3s ease-in-out infinite" : "none" }}
        >
          {/* Calculator header */}
          <div className="flex items-center gap-3 px-6 md:px-8 py-4 border-b border-[#1F2937] bg-[#EF444408]">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ backgroundColor: "#EF444412", color: "#EF4444" }}>
              <i className="ri-calculator-line"></i>
            </div>
            <div>
              <div className="text-sm font-black text-[#F8FAFC]">Live Risk Calculator</div>
              <div className="text-[10px] text-[#64748B]">Position sizing in real-time — adjust the sliders to see your numbers change instantly</div>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
              <span className="text-[9px] font-bold text-[#EF4444] uppercase tracking-wider">Live</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#1F2937]">
            {/* Input: Account Balance */}
            <div className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Account Balance</span>
                <span className="text-sm font-black text-[#F8FAFC]">${accountBalance.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min={500} max={100000} step={500}
                value={accountBalance}
                onChange={(e) => setAccountBalance(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: "#EF4444" }}
              />
              <div className="flex justify-between mt-1.5">
                <span className="text-[9px] text-[#374151]">$500</span>
                <span className="text-[9px] text-[#374151]">$100K</span>
              </div>
            </div>

            {/* Input: Risk % */}
            <div className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Risk Per Trade</span>
                <span className="text-sm font-black text-[#F8FAFC]">{riskPercent}%</span>
              </div>
              <input
                type="range"
                min={0.5} max={5} step={0.5}
                value={riskPercent}
                onChange={(e) => setRiskPercent(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: riskPercent <= 1 ? "#10B981" : riskPercent <= 2 ? "#F59E0B" : "#EF4444" }}
              />
              <div className="flex justify-between mt-1.5">
                <span className="text-[9px] text-[#374151]">0.5%</span>
                <span className="text-[9px]" style={{ color: riskPercent > 2 ? "#EF4444" : "#374151" }}>{riskPercent > 2 ? "⚠ High" : "5%"}</span>
              </div>
            </div>

            {/* Input: Stop-Loss */}
            <div className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Stop-Loss Pips</span>
                <span className="text-sm font-black text-[#F8FAFC]">{stopLossPips} pips</span>
              </div>
              <input
                type="range"
                min={5} max={100} step={5}
                value={stopLossPips}
                onChange={(e) => setStopLossPips(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: "#F59E0B" }}
              />
              <div className="flex justify-between mt-1.5">
                <span className="text-[9px] text-[#374151]">5</span>
                <span className="text-[9px] text-[#374151]">100</span>
              </div>
            </div>

            {/* Output panel */}
            <div className="p-5 md:p-6 bg-[#0B0F19]">
              <div className="text-[9px] font-black uppercase tracking-widest text-[#475569] mb-4">Your Numbers</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#64748B]">Max $ Risk</span>
                  <span
                    className="text-sm font-black"
                    style={{ color: riskPercent <= 1 ? "#10B981" : riskPercent <= 2 ? "#F59E0B" : "#EF4444" }}
                  >
                    ${maxRisk.toFixed(0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#64748B]">Lot Size</span>
                  <span className="text-sm font-black text-[#F8FAFC]">
                    {positionSize > 1 ? positionSize.toFixed(1) : positionSize.toFixed(3)} lots
                  </span>
                </div>
                <div className="h-px bg-[#1F2937]"></div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#64748B]">2R Target</span>
                  <span className="text-sm font-black text-[#10B981]">${rewardTarget.toFixed(0)}</span>
                </div>
                <div
                  className="text-[9px] font-semibold px-2.5 py-1 rounded-full text-center mt-1"
                  style={{
                    backgroundColor: riskPercent <= 1 ? "#10B98112" : riskPercent <= 2 ? "#F59E0B12" : "#EF444412",
                    color: riskPercent <= 1 ? "#10B981" : riskPercent <= 2 ? "#F59E0B" : "#EF4444",
                  }}
                >
                  {riskPercent <= 1 ? "Safe — Pro Level" : riskPercent <= 2 ? "Moderate Risk" : "High Risk — Reduce"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 8: Forex Tools — Command Center Layout                       */
/* ------------------------------------------------------------------ */

function ForexToolsModule() {
  const { ref, isVisible } = useScrollReveal(0.08);
  const [selectedTool, setSelectedTool] = useState(0);
  const [prevTool, setPrevTool] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const tool = forexTools[selectedTool];

  const startAutoRotate = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (isHovering) return;
    intervalRef.current = setInterval(() => {
      setTransitioning(true);
      setSelectedTool((prev) => {
        setPrevTool(prev);
        return (prev + 1) % forexTools.length;
      });
      setTimeout(() => setTransitioning(false), 180);
    }, 3000);
  };

  useEffect(() => {
    if (!isVisible) return;
    startAutoRotate();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isVisible, isHovering]);

  const handleSelect = (idx: number) => {
    if (idx === selectedTool) return;
    setTransitioning(true);
    setPrevTool(selectedTool);
    setTimeout(() => {
      setSelectedTool(idx);
      setTransitioning(false);
    }, 180);
    startAutoRotate();
  };

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      {isVisible && (
        <style>{`
          @keyframes toolListIn { from { opacity:0; transform:translateX(-20px); } to { opacity:1; transform:translateX(0); } }
          @keyframes toolPanelIn { from { opacity:0; transform:translateY(12px) scale(0.99); } to { opacity:1; transform:translateY(0) scale(1); } }
          @keyframes toolTagIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
          @keyframes toolBarGrow { from { width:0%; } to { width:100%; } }
        `}</style>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Module 8" badgeIcon="ri-tools-line" badgeColor="#14B8A6"
          title="Forex Tools"
          subtitle="Every successful trader builds a toolkit. These calculators and resources help you size positions, manage risk, track economic events, and make data-driven decisions instead of gut-feel guesses."
        />

        <div className="flex flex-col lg:flex-row gap-0 rounded-2xl border border-[#1F2937] overflow-hidden bg-[#0B0F19]">
          {/* Left: Tool List */}
          <div className="w-full lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-[#1F2937]">
            {/* List header */}
            <div className="px-5 py-4 border-b border-[#1F2937] bg-[#111827]">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-[#14B8A6]/12 flex items-center justify-center">
                  <i className="ri-tools-line text-[10px] text-[#14B8A6]"></i>
                </div>
                <span className="text-[11px] font-bold text-[#94A3B8] uppercase tracking-wider">8 Tools Available</span>
              </div>
            </div>

            {/* Tool list items */}
            <div className="flex flex-row lg:flex-col overflow-x-auto lg:overflow-x-visible">
              {forexTools.map((t, idx) => {
                const isSelected = selectedTool === idx;
                return (
                  <button
                    key={t.title}
                    onClick={() => handleSelect(idx)}
                    className="flex items-center gap-3 px-4 py-3.5 w-full text-left transition-all duration-200 cursor-pointer shrink-0 lg:shrink relative group border-r lg:border-r-0 lg:border-b border-[#1F2937] last:border-0"
                    style={{
                      backgroundColor: isSelected ? `${t.color}08` : "transparent",
                      animation: isVisible ? `toolListIn 0.4s ease ${idx * 0.05}s both` : "none",
                      opacity: isVisible ? undefined : 0,
                    }}
                  >
                    {/* Selected indicator */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-[3px] lg:rounded-r-full transition-all duration-200"
                      style={{ backgroundColor: isSelected ? t.color : "transparent" }}
                    />

                    {/* Number */}
                    <span
                      className="text-[10px] font-black w-5 shrink-0 tabular-nums"
                      style={{ color: isSelected ? t.color : "#475569" }}
                    >
                      {(idx + 1).toString().padStart(2, "0")}
                    </span>

                    {/* Icon */}
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-sm transition-colors duration-200"
                      style={{
                        backgroundColor: isSelected ? `${t.color}18` : `${t.color}08`,
                        color: isSelected ? t.color : "#475569",
                      }}
                    >
                      <i className={t.icon}></i>
                    </div>

                    {/* Title */}
                    <span
                      className="text-[12px] font-semibold whitespace-nowrap transition-colors duration-200"
                      style={{ color: isSelected ? "#F8FAFC" : "#64748B" }}
                    >
                      {t.title}
                    </span>

                    {/* Arrow */}
                    <i
                      className="ri-arrow-right-s-line text-sm ml-auto transition-all duration-200"
                      style={{ color: isSelected ? tool.color : "transparent" }}
                    ></i>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Tool Detail Panel */}
          <div className="flex-1 min-w-0">
            <div
              className="h-full"
              style={{
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? "translateY(8px)" : "translateY(0)",
                transition: "opacity 0.18s ease, transform 0.18s ease",
              }}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {/* Panel Header */}
              <div
                className="px-6 md:px-8 py-6 border-b border-[#1F2937]"
                style={{ backgroundColor: `${tool.color}04` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-xl"
                    style={{ backgroundColor: `${tool.color}18`, color: tool.color }}
                  >
                    <i className={tool.icon}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${tool.color}12`, color: tool.color }}
                      >
                        Tool {(selectedTool + 1).toString().padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-black text-[#F8FAFC] mb-2">{tool.title}</h3>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{tool.description}</p>
                  </div>
                </div>

                {/* Color progress bar */}
                <div className="mt-5 h-[2px] bg-[#1F2937] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${((selectedTool + 1) / forexTools.length) * 100}%`,
                      backgroundColor: tool.color,
                      transition: "width 0.4s cubic-bezier(0.16,1,0.3,1), background-color 0.2s",
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[9px] text-[#475569]">{selectedTool + 1} of {forexTools.length} tools</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleSelect(Math.max(0, selectedTool - 1))}
                      disabled={selectedTool === 0}
                      className="w-6 h-6 rounded-md flex items-center justify-center transition-all cursor-pointer"
                      style={{ backgroundColor: selectedTool > 0 ? `${tool.color}12` : "#1F2937", color: selectedTool > 0 ? tool.color : "#374151" }}
                    >
                      <i className="ri-arrow-left-s-line text-xs"></i>
                    </button>
                    <button
                      onClick={() => handleSelect(Math.min(forexTools.length - 1, selectedTool + 1))}
                      disabled={selectedTool === forexTools.length - 1}
                      className="w-6 h-6 rounded-md flex items-center justify-center transition-all cursor-pointer"
                      style={{ backgroundColor: selectedTool < forexTools.length - 1 ? `${tool.color}12` : "#1F2937", color: selectedTool < forexTools.length - 1 ? tool.color : "#374151" }}
                    >
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* How To Use + Pro Tip */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#1F2937]">
                {/* How To Use */}
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-sm"
                      style={{ backgroundColor: `${tool.color}12`, color: tool.color }}
                    >
                      <i className="ri-play-circle-line"></i>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: tool.color }}>
                      How To Use
                    </span>
                  </div>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{tool.usage}</p>

                  {/* Example box */}
                  <div
                    className="mt-5 rounded-xl p-4 border"
                    style={{ backgroundColor: `${tool.color}05`, borderColor: `${tool.color}15` }}
                  >
                    <div className="flex items-start gap-2">
                      <i className="ri-terminal-box-line text-sm shrink-0 mt-0.5" style={{ color: tool.color }}></i>
                      <div>
                        <div className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: tool.color }}>Example</div>
                        <p className="text-xs text-[#64748B] leading-relaxed font-mono">{tool.usage.split(".")[1]?.trim() ?? tool.usage.split(".")[0]}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pro Tip */}
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-sm"
                      style={{ backgroundColor: `${tool.color}12`, color: tool.color }}
                    >
                      <i className="ri-lightbulb-flash-line"></i>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: tool.color }}>
                      Pro Tip
                    </span>
                  </div>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{tool.tip}</p>

                  {/* All tools quick-jump at bottom */}
                  <div className="mt-6">
                    <div className="text-[9px] font-bold text-[#475569] uppercase tracking-wider mb-3">Quick Jump</div>
                    <div className="flex flex-wrap gap-1.5">
                      {forexTools.map((t, idx) => (
                        <button
                          key={t.title}
                          onClick={() => handleSelect(idx)}
                          className="text-[10px] px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap"
                          style={{
                            backgroundColor: selectedTool === idx ? `${t.color}12` : "transparent",
                            borderColor: selectedTool === idx ? `${t.color}35` : "#1F2937",
                            color: selectedTool === idx ? t.color : "#475569",
                            fontWeight: selectedTool === idx ? 700 : 400,
                          }}
                        >
                          {t.title.replace(" Calculator", "").replace(" Rate", "")}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Stats Banner                                                       */
/* ------------------------------------------------------------------ */

function StatsBanner() {
  const { ref, isVisible } = useScrollReveal(0.15);

  const stats = [
    { value: 75, suffix: "T", label: "Daily Forex Volume", icon: "ri-global-line", color: "#10B981" },
    { value: 170, suffix: "+", label: "Currency Pairs Available", icon: "ri-exchange-line", color: "#F59E0B" },
    { value: 5, suffix: "M+", label: "XM Traders Worldwide", icon: "ri-user-line", color: "#EC4899" },
    { value: 8, suffix: "", label: "Academy Modules", icon: "ri-book-open-line", color: "#8B5CF6" },
  ];

  return (
    <section ref={ref} className={`py-16 md:py-20 bg-[#0B0F19] border-t border-[#1F2937] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center group cursor-default">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 text-lg transition-all duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${stat.color}12`, color: stat.color }}
              >
                <i className={stat.icon}></i>
              </div>
              <div className="text-2xl md:text-3xl font-black text-[#F8FAFC] mb-1">
                <AnimatedCountUp target={stat.value} isActive={isVisible} suffix={stat.suffix} />
              </div>
              <div className="text-[11px] text-[#64748B]">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Final CTA                                                          */
/* ------------------------------------------------------------------ */

function FinalCTA() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden bg-[#0B0F19]">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#7C3AED]/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-[#F59E0B]/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#F8FAFC] mb-5">
          Knowledge is Your Edge.
          <br />
          <span className="text-gradient">Start Applying It Today.</span>
        </h2>
        <p className="text-base text-[#94A3B8] mb-10 max-w-xl mx-auto leading-relaxed">
          Ready to put theory into practice? Compare XM against 5 other leading brokers with live account data, real spreads, and verified execution speeds.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/compare"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-sm font-bold rounded-xl transition-all hover:shadow-xl hover:shadow-[#7C3AED]/20 whitespace-nowrap"
          >
            Start Comparing Brokers
            <i className="ri-arrow-right-line"></i>
          </Link>
          <Link
            to="/rankings"
            className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#374151] hover:border-[#7C3AED] text-[#F8FAFC] text-sm font-bold rounded-xl transition-all hover:bg-[#1F2937] whitespace-nowrap"
          >
            View Platform Rankings
            <i className="ri-bar-chart-grouped-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Export                                                        */
/* ------------------------------------------------------------------ */

export default function Academy() {
  useSEO({
    /* @replace:academy-title */

    title: 'Forex Academy 2026 | Broker Guides, Trading Knowledge & Comparison Insights',
    /* @replace:academy-description */

    description: 'Build your forex knowledge with structured learning modules covering market fundamentals, trading platforms, broker comparisons, technical analysis, risk management, and practical trading tools.',
    /* @replace:academy-keywords */

    keywords: 'Forex Academy, forex broker education, forex trading guides, broker comparison guide, forex platform guide, forex trading knowledge',
    /* @replace:academy-canonical */

    canonical: '/academy',
  });

  return (
    <div className="min-h-screen bg-[#111827]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Forex Academy — Complete Trading Education for Beginners to Advanced Traders",
            "description": "Comprehensive forex education hub covering basics, platform guides, broker knowledge, XM broker deep dive, comparison education, market analysis, risk management, and essential trading tools.",
            "keywords": "forex education, learn forex trading, XM broker academy, forex basics, trading platform guide, risk management, forex analysis, broker comparison guide",
          }),
        }}
      />

      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden bg-[#0B0F19]">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-[#10B981]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#F59E0B]/5 rounded-full blur-[100px]" />

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-6">
            <i className="ri-graduation-cap-line text-[#10B981]"></i>
            <span className="text-[10px] font-bold text-[#10B981] uppercase tracking-wider">Free Trading Education</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-[#F8FAFC] mb-6 tracking-tight leading-tight">
            Forex Academy
          </h1>
          <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto mb-10 leading-relaxed">
            From your first pip to advanced risk management — everything you need to trade forex with confidence.
            Eight comprehensive modules, zero cost, no sign-up required.
          </p>

          {/* Quick nav pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { label: "Forex Basics", href: "#forex-basics", icon: "ri-graduation-cap-line", color: "#10B981" },
              { label: "Platforms", href: "#platform-education", icon: "ri-smartphone-line", color: "#3B82F6" },
              { label: "Brokers", href: "#broker-education", icon: "ri-bank-line", color: "#F59E0B" },
              { label: "XM Deep Dive", href: "#xm-education", icon: "ri-star-line", color: "#EC4899" },
              { label: "Comparison", href: "#comparison-education", icon: "ri-arrow-left-right-line", color: "#8B5CF6" },
              { label: "Analysis", href: "#analysis-education", icon: "ri-line-chart-line", color: "#F97316" },
              { label: "Risk Mgmt", href: "#risk-management", icon: "ri-shield-flash-line", color: "#EF4444" },
              { label: "Tools", href: "#forex-tools", icon: "ri-tools-line", color: "#14B8A6" },
            ].map((nav) => (
              <a
                key={nav.label}
                href={nav.href}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#1F2937] border border-[#374151] hover:border-[#F8FAFC]/20 text-[11px] text-[#94A3B8] hover:text-[#F8FAFC] transition-all whitespace-nowrap"
                style={{ borderColor: `transparent` }}
              >
                <i className={`${nav.icon} text-[10px]`} style={{ color: nav.color }}></i>
                {nav.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <StatsBanner />

      <div id="forex-basics"><ForexBasicsModule /></div>
      <div id="platform-education"><PlatformEducationModule /></div>
      <div id="broker-education"><BrokerEducationModule /></div>
      <div id="xm-education"><XmEducationModule /></div>
      <div id="comparison-education"><ComparisonEducationModule /></div>
      <div id="analysis-education"><AnalysisEducationModule /></div>
      <div id="risk-management"><RiskManagementModule /></div>
      <div id="forex-tools"><ForexToolsModule /></div>

      <FinalCTA />
    </div>
  );
}